import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Plus, Trash2, Loader2, QrCode, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { printQRFromDOM } from '@/lib/printQR';

export default function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('cashier');

  const isOwner = user?.role === 'owner';

  const fetchStaff = async () => {
    try {
      const res = await api.get('/staff');
      setStaff(res.data.staff);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error('Failed to load staff');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.post('/staff', {
        name: newName,
        email: newEmail,
        password: newPassword,
        role: newRole
      });
      toast.success('Staff member added');
      setShowAdd(false);
      setNewName('');
      setNewEmail('');
      setNewPassword('');
      setNewRole('cashier');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add staff');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/staff/${deleteTarget.user_id}`);
      toast.success('Staff member removed');
      setDeleteTarget(null);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove staff');
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'owner') return 'bg-orange-50 text-orange-700 border-orange-200';
    if (role === 'admin') return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Staff
          </h1>
          <p className="text-sm text-slate-500 mt-1">{staff.length} team members</p>
        </div>
        {isOwner && (
          <Button
            onClick={() => setShowAdd(true)}
            className="h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium"
            data-testid="add-staff-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
        )}
      </div>

      {/* Staff List */}
      {staff.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No staff members yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-card border border-border" data-testid="staff-list">
          {staff.map((member, i) => (
            <div
              key={member.user_id}
              className={`flex items-center justify-between px-4 py-4 ${i < staff.length - 1 ? 'border-b border-slate-100' : ''}`}
              data-testid={`staff-row-${member.user_id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-slate-600">
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{member.name}</div>
                  <div className="text-xs text-slate-400 truncate">{member.email}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${getRoleBadgeClass(member.role)}`}>
                  {member.role}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQrTarget(member)}
                  className="text-slate-400 hover:text-orange-600 h-8 w-8 p-0"
                  title="Permanent QR code"
                  data-testid={`qr-staff-${member.user_id}`}
                >
                  <QrCode className="w-4 h-4" />
                </Button>
                {isOwner && member.role !== 'owner' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTarget(member)}
                    className="text-slate-400 hover:text-red-600 h-8 w-8 p-0"
                    data-testid={`delete-staff-${member.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Staff Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
            <DialogDescription>Create a new account for a team member.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                required
                className="h-11"
                data-testid="new-staff-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                required
                className="h-11"
                data-testid="new-staff-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                className="h-11"
                data-testid="new-staff-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="h-11" data-testid="new-staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={addLoading}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="submit-new-staff-btn"
              >
                {addLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {addLoading ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-staff-dialog">
          <DialogHeader>
            <DialogTitle>Remove Staff Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {deleteTarget?.name}? They will lose access to the store.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-staff-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {deleting ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Permanent QR Code Dialog */}
      <Dialog open={!!qrTarget} onOpenChange={(open) => !open && setQrTarget(null)}>
        <DialogContent data-testid="staff-qr-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Permanent QR Code</DialogTitle>
            <DialogDescription>
              {qrTarget?.name}'s fixed QR code. Print and stick at their checkout station.
            </DialogDescription>
          </DialogHeader>
          {qrTarget && (
            <div className="text-center py-4">
              <div className="inline-block p-3 bg-white border border-border" id="staff-qr-container">
                <QRCodeSVG
                  value={`${window.location.origin}/pay/${user?.store_id}/${qrTarget.user_id}`}
                  size={200}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-3 font-medium">{qrTarget.name} — {qrTarget.role}</p>
              <p className="text-[10px] text-muted-foreground mt-1 font-mono break-all">
                {`${window.location.origin}/pay/${user?.store_id}/${qrTarget.user_id}`}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const url = `${window.location.origin}/pay/${user?.store_id}/${qrTarget?.user_id}`;
                await navigator.clipboard.writeText(url);
                toast.success('QR link copied!');
              }}
              data-testid="copy-staff-qr-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy link
            </Button>
            <Button
              onClick={() => {
                const url = `${window.location.origin}/pay/${user?.store_id}/${qrTarget?.user_id}`;
                printQRFromDOM('#staff-qr-container', qrTarget?.name, url);
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="print-staff-qr-btn"
            >
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
